import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';
import { toast } from 'sonner';
import { Mail, Send, History, Loader2, Eye, AlertCircle, CheckCircle2, MailOpen } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TEMPLATES = [
  { key: 'documents_required',   label: 'Documents Required' },
  { key: 'interview_scheduled',  label: 'Counselling Call Scheduled' },
  { key: 'eligibility_confirmed',label: 'Eligibility Confirmed' },
  { key: 'congratulations',      label: 'Congratulations / Welcome' },
  { key: 'gentle_nudge',         label: 'Gentle Nudge / Reminder' },
  { key: 'custom',               label: 'Custom Email (write your own)' },
];

const getAuthHeaders = () => {
  const token = localStorage.getItem('ohcampus_token');
  return { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
};

const formatDateTime = (s) => {
  if (!s) return '-';
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

/**
 * <SendScholarshipEmailButton application={app} />
 * Renders a button + modal that lets the user pick a template and send a follow-up
 * email to the applicant. Re-uses the same backend endpoint regardless of role
 * (admin / team_lead / admission_manager / counselor).
 */
export function SendScholarshipEmailButton({ application, onSent, variant = 'outline', size = 'sm', className = '', label = 'Send Email' }) {
  const [open, setOpen] = useState(false);
  const [templateKey, setTemplateKey] = useState('documents_required');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);

  const reset = () => {
    setTemplateKey('documents_required');
    setSubject('');
    setBody('');
    setSending(false);
  };

  const handleSend = async () => {
    if (!application?.id) { toast.error('Missing application id'); return; }
    if (templateKey === 'custom' && !body.trim()) {
      toast.error('Please enter a message body for the custom email.');
      return;
    }
    setSending(true);
    try {
      const r = await fetch(`${API_URL}/api/scholarship-applications/${application.id}/send-email`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          template_key: templateKey,
          subject: subject || undefined,
          body: templateKey === 'custom' ? body : undefined,
        }),
      });
      const data = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success('Email sent.');
        setOpen(false);
        reset();
        if (typeof onSent === 'function') onSent(data);
      } else {
        toast.error(data?.detail || 'Failed to send email');
      }
    } catch (err) {
      toast.error('Network error while sending email');
    } finally {
      setSending(false);
    }
  };

  const isDisabled = !application?.email;

  return (
    <>
      <Button
        type="button"
        variant={variant}
        size={size}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        disabled={isDisabled}
        title={isDisabled ? 'Applicant has no email on file' : 'Send a branded follow-up email to the applicant'}
        className={`gap-2 ${className}`}
        data-testid={`send-email-btn-${application?.id || 'na'}`}
      >
        <Mail className="h-4 w-4" />
        {label}
      </Button>

      <Dialog open={open} onOpenChange={(v) => { if (!sending) { setOpen(v); if (!v) reset(); } }}>
        <DialogContent className="max-w-lg" data-testid="send-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5 text-[#0066CC]" />
              Send Follow-up Email
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="bg-slate-50 px-3 py-2 rounded text-sm">
              <p><span className="text-[#475569]">To:</span> <span className="font-medium">{application?.name || '-'}</span> &lt;{application?.email || 'no email'}&gt;</p>
              <p><span className="text-[#475569]">App #:</span> <span className="font-mono">{application?.application_number || application?.id || '-'}</span></p>
            </div>

            <div>
              <label className="text-sm font-medium text-[#0F172A] block mb-1.5">Template</label>
              <Select value={templateKey} onValueChange={setTemplateKey}>
                <SelectTrigger data-testid="email-template-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATES.map((t) => (
                    <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-[#0F172A] block mb-1.5">Subject (optional override)</label>
              <Input
                type="text"
                placeholder="Leave blank to use the template subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="email-subject-input"
              />
            </div>

            {templateKey === 'custom' && (
              <div>
                <label className="text-sm font-medium text-[#0F172A] block mb-1.5">Message Body</label>
                <Textarea
                  rows={6}
                  placeholder="Type your message... line breaks are preserved."
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  data-testid="email-body-textarea"
                />
                <p className="text-xs text-[#475569] mt-1">Wrapped in the OhCampus branded email layout.</p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={sending}>Cancel</Button>
            <Button
              onClick={handleSend}
              disabled={sending}
              className="gap-2"
              data-testid="send-email-submit"
            >
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {sending ? 'Sending...' : 'Send Email'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * <ScholarshipEmailHistory applicationId={id} refreshKey={N} />
 * Renders the email log for an application. Pass `refreshKey` (increment after a send)
 * to force a reload.
 */
export function ScholarshipEmailHistory({ applicationId, refreshKey = 0 }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [previewLogId, setPreviewLogId] = useState(null);

  const load = useCallback(async () => {
    if (!applicationId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/scholarship-applications/${applicationId}/email-log`, {
        headers: getAuthHeaders(),
      });
      if (r.ok) {
        const data = await r.json();
        setRows(data?.data || []);
      } else {
        setRows([]);
      }
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div data-testid="email-history-panel">
      <div className="flex items-center gap-2 mb-2">
        <History className="h-4 w-4 text-[#0066CC]" />
        <h3 className="font-medium text-[#0F172A] text-sm">Email History</h3>
        <Button
          variant="ghost"
          size="sm"
          onClick={load}
          disabled={loading}
          className="ml-auto h-7 px-2 text-xs"
          data-testid="email-history-refresh"
        >
          {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : 'Refresh'}
        </Button>
      </div>

      <div className="bg-slate-50 rounded-lg p-2 max-h-56 overflow-y-auto border border-slate-200">
        {loading && rows.length === 0 ? (
          <p className="text-xs text-[#475569] text-center py-3">Loading...</p>
        ) : rows.length === 0 ? (
          <p className="text-xs text-[#475569] text-center py-3">No emails sent for this application yet.</p>
        ) : (
          <ul className="divide-y divide-slate-200">
            {rows.map((row) => (
              <li key={row.id} className="py-2 px-2 flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#0F172A] truncate">{row.subject || row.email_type}</p>
                  <p className="text-[11px] text-[#475569] truncate">
                    <span className="font-mono">{row.email_type}</span>
                    {' · to '}{row.to_email}
                    {' · '}{formatDateTime(row.sent_at)}
                    {row.triggered_by ? <> {' · '}<span className="italic">{row.triggered_by}</span></> : null}
                  </p>
                  {row.opened_at && (
                    <p className="text-[11px] text-blue-700 truncate flex items-center gap-1">
                      <MailOpen className="h-3 w-3" />
                      opened {formatDateTime(row.opened_at)}
                      {row.open_count > 1 ? <> · {row.open_count} views</> : null}
                    </p>
                  )}
                  {row.status === 'failed' && row.error_message && (
                    <p className="text-[11px] text-red-600 mt-0.5">Error: {row.error_message}</p>
                  )}
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  title="Preview email"
                  onClick={() => setPreviewLogId(row.id)}
                  data-testid={`history-preview-btn-${row.id}`}
                >
                  <Eye className="h-3.5 w-3.5" />
                </Button>
                <Badge className={row.status === 'sent' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                  {row.status}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>

      <EmailPreviewModal
        logId={previewLogId}
        open={Boolean(previewLogId)}
        onOpenChange={(v) => { if (!v) setPreviewLogId(null); }}
      />
    </div>
  );
}

export default { SendScholarshipEmailButton, ScholarshipEmailHistory };
// EmailPreviewModal and LastEmailCell are defined below and exported as named exports.

/**
 * <EmailPreviewModal logId open onOpenChange />
 * Loads the rendered HTML body of a logged email and renders it inside a
 * sandboxed iframe (srcdoc). Used by both the "Last Email" column preview
 * icon and the per-row history items.
 */
export function EmailPreviewModal({ logId, open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !logId) {
      setData(null);
      setError(null);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API_URL}/api/scholarship-applications/email-log/${logId}/preview`, {
          headers: getAuthHeaders(),
        });
        const body = await r.json().catch(() => ({}));
        if (cancelled) return;
        if (!r.ok) {
          setError(body?.detail || 'Failed to load email');
        } else {
          setData(body);
        }
      } catch (e) {
        if (!cancelled) setError('Network error while loading email');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, logId]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden p-0" data-testid="email-preview-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Eye className="h-5 w-5 text-[#0066CC]" />
            Email Preview
            {data?.status === 'sent' && (
              <Badge className="bg-green-100 text-green-800">sent</Badge>
            )}
            {data?.status === 'failed' && (
              <Badge className="bg-red-100 text-red-800">failed</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {data && (
          <div className="px-5 py-3 bg-slate-50 border-b text-sm">
            <p><span className="text-[#475569]">Subject:</span> <span className="font-medium">{data.subject || data.email_type}</span></p>
            <p className="text-xs text-[#475569] mt-1">
              <span className="font-mono">{data.email_type}</span>
              {' · to '}{data.to_email}
              {' · '}{formatDateTime(data.sent_at)}
              {data.triggered_by ? <> {' · '}<span className="italic">{data.triggered_by}</span></> : null}
            </p>
            {data.opened_at ? (
              <p className="text-xs text-blue-700 mt-1 flex items-center gap-1">
                <MailOpen className="h-3.5 w-3.5" />
                Opened {formatDateTime(data.opened_at)}{data.open_count > 1 ? ` · ${data.open_count} views` : ''}
              </p>
            ) : data.status === 'sent' ? (
              <p className="text-xs text-slate-500 mt-1 flex items-center gap-1">
                <Mail className="h-3.5 w-3.5" />
                Not opened yet (recipient may have images blocked)
              </p>
            ) : null}
            {data.status === 'failed' && data.error_message && (
              <p className="text-xs text-red-600 mt-1">Error: {data.error_message}</p>
            )}
          </div>
        )}

        <div className="px-5 py-3 overflow-y-auto" style={{ maxHeight: '60vh' }}>
          {loading && (
            <div className="flex items-center justify-center py-10 text-[#475569]">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading email...
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-red-700 bg-red-50 p-3 rounded">
              <AlertCircle className="h-5 w-5" /> {error}
            </div>
          )}
          {data && !loading && (
            data.body_html ? (
              <iframe
                title="Email body preview"
                srcDoc={data.body_html}
                sandbox=""
                className="w-full border rounded"
                style={{ minHeight: '55vh' }}
                data-testid="email-preview-iframe"
              />
            ) : (
              <div className="text-sm text-[#475569] italic py-4">
                (The HTML body was not stored for this email — it was sent before preview support was added.)
              </div>
            )
          )}
        </div>

        <DialogFooter className="px-5 py-3 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * <LastEmailCell lastEmail />
 * Compact cell for the applications list table. Shows:
 *  - "—" if no email has been sent
 *  - status badge + relative date + Preview icon button
 * Clicking the preview icon opens <EmailPreviewModal /> for the underlying log row.
 */
export function LastEmailCell({ lastEmail }) {
  const [previewOpen, setPreviewOpen] = useState(false);

  if (!lastEmail || !lastEmail.id) {
    return <span className="text-xs text-[#94a3b8]">—</span>;
  }
  const sentDate = lastEmail.sent_at ? formatDateTime(lastEmail.sent_at) : '';
  const openedDate = lastEmail.opened_at ? formatDateTime(lastEmail.opened_at) : '';
  const isSent = lastEmail.status === 'sent';
  const isOpened = Boolean(lastEmail.opened_at);
  const friendlyType = (lastEmail.email_type || '').replace(/^status_/, 'Status: ').replace(/^custom_/, '').replace(/_/g, ' ');

  return (
    <>
      <div className="flex items-center gap-2 min-w-0">
        <div className="flex flex-col gap-1 shrink-0">
          <Badge
            className={isSent ? 'bg-green-100 text-green-800 gap-1' : 'bg-red-100 text-red-800 gap-1'}
            data-testid={`last-email-status-${lastEmail.id}`}
          >
            {isSent ? <CheckCircle2 className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
            {lastEmail.status}
          </Badge>
          {isSent && (
            isOpened ? (
              <Badge
                className="bg-blue-100 text-blue-800 gap-1"
                title={`Opened ${openedDate}${lastEmail.open_count > 1 ? ` · ${lastEmail.open_count} views` : ''}`}
                data-testid={`last-email-opened-${lastEmail.id}`}
              >
                <MailOpen className="h-3 w-3" />
                opened{lastEmail.open_count > 1 ? ` ×${lastEmail.open_count}` : ''}
              </Badge>
            ) : (
              <Badge
                className="bg-slate-100 text-slate-600 gap-1"
                title="Pixel not yet fetched (recipient may have images blocked)"
                data-testid={`last-email-not-opened-${lastEmail.id}`}
              >
                <Mail className="h-3 w-3" />
                not opened
              </Badge>
            )
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-[#0F172A] truncate" title={lastEmail.subject || friendlyType}>
            {lastEmail.subject || friendlyType || '-'}
          </p>
          <p className="text-[10px] text-[#475569] truncate" title={sentDate}>sent {sentDate}</p>
          {isOpened && (
            <p className="text-[10px] text-blue-700 truncate" title={openedDate}>opened {openedDate}</p>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 shrink-0"
          onClick={(e) => { e.stopPropagation(); setPreviewOpen(true); }}
          title="Preview email"
          data-testid={`preview-email-btn-${lastEmail.id}`}
        >
          <Eye className="h-3.5 w-3.5" />
        </Button>
      </div>

      <EmailPreviewModal logId={lastEmail.id} open={previewOpen} onOpenChange={setPreviewOpen} />
    </>
  );
}
